import React, { createContext, useContext, useState, useEffect } from "react";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase/firebaseConfig"; // adjust path as needed

const CartContext = createContext();

export function CartProvider({ children }) {
  const [cart, setCart] = useState(() => {
    const saved = localStorage.getItem("cart");
    return saved ? JSON.parse(saved) : [];
  });
  const [pastOrders, setPastOrders] = useState(() => {
    const saved = localStorage.getItem("pastOrders");
    return saved ? JSON.parse(saved) : [];
  });
  const [favourites, setFavourites] = useState({ restaurants: [], dishes: [] });
  const [recommendedDishes, setRecommendedDishes] = useState([]);
  const [rewardPoints, setRewardPoints] = useState(0);

  useEffect(() => {
    localStorage.setItem("cart", JSON.stringify(cart));
    localStorage.setItem("pastOrders", JSON.stringify(pastOrders));
  }, [cart, pastOrders]);

  useEffect(() => {
    const sampleDishes = [
      { id: "m101", name: "Margherita Pizza", price: 155, restaurant: "IlovePizza", category: "Pizza", img: "https://source.unsplash.com/300x200/?margherita" },
      { id: "d5", name: "Paneer Roll", price: 80 * 1.2, restaurant: "Sharma Fast Food", category: "Street", img: "https://source.unsplash.com/300x200/?paneer-roll" },
      { id: "d9", name: "Cold Coffee", price: 120 * 1.2, restaurant: "Coffee Express", category: "Drinks", img: "https://source.unsplash.com/300x200/?cold-coffee" },
    ];
    setRecommendedDishes(sampleDishes);
  }, []);

  const addToCart = (dish, quantity = 1, customizations = {}, schedule = {}) => {
    setCart((prev) => {
      const existing = prev.find((item) => item.id === dish.id);
      if (existing) {
        return prev.map((item) =>
          item.id === dish.id
            ? { ...item, quantity: item.quantity + quantity, customizations, schedule }
            : item
        );
      }
      return [
        ...prev,
        {
          id: dish.id,
          name: dish.name,
          price: dish.price,
          img: dish.img || "/zoomo-mascot.png",
          quantity,
          customizations,
          schedule,
        },
      ];
    });
    setRewardPoints((prev) => prev + 10 * quantity);
  };

  const decreaseQuantity = (id) => {
    setCart((prev) => {
      const item = prev.find((item) => item.id === id);
      if (!item) return prev;
      if (item.quantity <= 1) {
        return prev.filter((item) => item.id !== id);
      }
      return prev.map((item) =>
        item.id === id ? { ...item, quantity: item.quantity - 1 } : item
      );
    });
    setRewardPoints((prev) => Math.max(0, prev - 10));
  };

  const removeFromCart = (id) => {
    setCart((prev) => prev.filter((item) => item.id !== id));
    setRewardPoints((prev) => Math.max(0, prev - 10));
  };

  const updateQuantity = (id, newQuantity) => {
    setCart((prev) => {
      const item = prev.find((item) => item.id === id);
      if (!item) return prev;
      const quantityChange = newQuantity - item.quantity;
      if (newQuantity <= 0) {
        return prev.filter((item) => item.id !== id);
      }
      return prev.map((item) =>
        item.id === id ? { ...item, quantity: newQuantity } : item
      );
    });
    setRewardPoints((prev) => {
      const item = cart.find((item) => item.id === id);
      if (!item) return prev;
      const quantityChange = newQuantity - item.quantity;
      return Math.max(0, prev + (quantityChange > 0 ? 10 : -10) * Math.abs(quantityChange));
    });
  };

  const clearCart = () => {
    setCart([]);
    setRewardPoints(0);
  };

  const checkout = async (address, paymentDetails, specialInstructions, userId = "guest") => {
    const order = {
      userId,
      items: [...cart],
      total: cart.reduce((sum, item) => sum + item.price * item.quantity, 0),
      address,
      paymentDetails,
      specialInstructions,
      status: "pending",
      createdAt: serverTimestamp(),
    };

    try {
      const docRef = await addDoc(collection(db, "orders"), order);
      setPastOrders((prev) => [...prev, { ...order, id: docRef.id }]);
      clearCart();
      return { success: true, orderId: docRef.id };
    } catch (error) {
      console.error("Failed to place order:", error);
      return { success: false, error };
    }
  };

  const reorder = (order) => {
    order.items.forEach((item) => {
      addToCart(
        { id: item.id, name: item.name, price: item.price, img: item.img },
        item.quantity,
        item.customizations || {},
        item.schedule || {}
      );
    });
  };

  const toggleFavouriteDish = (dishId) => {
    setFavourites((prev) => ({
      ...prev,
      dishes: prev.dishes.includes(dishId)
        ? prev.dishes.filter((id) => id !== dishId)
        : [...prev.dishes, dishId],
    }));
  };

  const toggleFavouriteRestaurant = (restaurantId) => {
    setFavourites((prev) => ({
      ...prev,
      restaurants: prev.restaurants.includes(restaurantId)
        ? prev.restaurants.filter((id) => id !== restaurantId)
        : [...prev.restaurants, restaurantId],
    }));
  };

  return (
    <CartContext.Provider
      value={{
        cart,
        addToCart,
        decreaseQuantity,
        removeFromCart,
        updateQuantity,
        clearCart,
        checkout,
        pastOrders,
        reorder,
        favourites,
        toggleFavouriteDish,
        toggleFavouriteRestaurant,
        recommendedDishes,
        rewardPoints,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  return useContext(CartContext);
}